import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Link,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import ContentCopy from '@mui/icons-material/ContentCopy';
import FileCopy from '@mui/icons-material/FileCopy';
import { Bookmark } from '../types/bookmarks';
import { copyToClipboard } from '../utils/clipboardService';
import { useWebAuthn } from '../contexts/WebAuthnContext';
import { stripHtml, normalizeUrl } from '../utils/bookmarkUtils';

const DRAWER_MIN_WIDTH = 300;
const DRAWER_MAX_WIDTH = 900;
const DRAWER_DEFAULT_WIDTH = 400;

interface BookmarkPreviewDrawerProps {
  bookmark: Bookmark | null;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
}

const BookmarkPreviewDrawer: React.FC<BookmarkPreviewDrawerProps> = ({
  bookmark,
  open,
  onClose,
  onEdit,
}) => {
  const { setSnackbar } = useWebAuthn();
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_DEFAULT_WIDTH);
  const isResizing = useRef(false);

  // Android back button support: push a history entry when drawer opens,
  // listen for popstate to close it
  useEffect(() => {
    if (open) {
      window.history.pushState({ drawerOpen: true }, '');
      const handlePopState = () => {
        onClose();
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [open, onClose]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      isResizing.current = true;

      const handleMove = (event: MouseEvent | TouchEvent) => {
        if (!isResizing.current) return;
        const clientX =
          'touches' in event ? event.touches[0].clientX : event.clientX;
        const newWidth = Math.min(
          DRAWER_MAX_WIDTH,
          Math.max(DRAWER_MIN_WIDTH, window.innerWidth - clientX),
        );
        setDrawerWidth(newWidth);
      };

      const handleEnd = () => {
        isResizing.current = false;
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleEnd);
    },
    [],
  );

  if (!bookmark) return null;

  const handleCopyUrl = async () => {
    const success = await copyToClipboard(bookmark.bookmark_url);
    setSnackbar({
      open: true,
      message: success ? 'URL copied to clipboard' : 'Copy failed',
      severity: success ? 'success' : 'error',
    });
  };

  const handleCopyNotes = async () => {
    const plainText = stripHtml(bookmark.bookmark_notes);
    const success = await copyToClipboard(plainText);
    setSnackbar({
      open: true,
      message: success ? 'Notes copied to clipboard' : 'Copy failed',
      severity: success ? 'success' : 'error',
    });
  };

  const hasNotes = !!stripHtml(bookmark.bookmark_notes);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: drawerWidth },
          p: 0,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', height: '100%' }}>
        {/* Resize handle on the left edge */}
        <Box
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          sx={{
            width: 4,
            cursor: 'col-resize',
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
            '&:hover': {
              bgcolor: 'primary.main',
              opacity: 0.3,
            },
            transition: 'background-color 0.2s',
          }}
        />

        {/* Drawer content */}
        <Box sx={{ flex: 1, p: 2, overflowY: 'auto', overflowX: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
              }}
            >
              {bookmark.bookmark_name}
            </Typography>
            <IconButton onClick={onClose} aria-label="Close preview">
              <CloseIcon />
            </IconButton>
          </Box>

          {normalizeUrl(bookmark.bookmark_url) && (
            <Link
              href={normalizeUrl(bookmark.bookmark_url)!}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              variant="body2"
              sx={{ display: 'block', mb: 2, wordBreak: 'break-all' }}
            >
              {bookmark.bookmark_url}
            </Link>
          )}

          <Divider sx={{ mb: 2 }} />

          {hasNotes && (
            <Box
              sx={{
                '& p': { marginTop: 0 },
                '& a': { color: 'primary.main' },
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                mb: 2,
              }}
              dangerouslySetInnerHTML={{ __html: bookmark.bookmark_notes }}
            />
          )}

          {!hasNotes && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, fontStyle: 'italic' }}
            >
              [No notes]
            </Typography>
          )}

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Edit />}
              onClick={onEdit}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={handleCopyUrl}
            >
              Copy URL
            </Button>
            {hasNotes && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileCopy />}
                onClick={handleCopyNotes}
              >
                Copy Notes
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default BookmarkPreviewDrawer;
